// @flow

import React, {useState, useCallback, useEffect, useRef, useMemo} from 'react';
import queryString from 'query-string';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {
  CircularProgress,
  Divider,
  Grid,
  makeStyles,
  Typography,
} from '@material-ui/core';
import CustomModal from '../../../../elements/CustomModal/CustomModal';
import {colors} from '../../../../assets/styles/colors';

import {useDebounce} from '../../../../utils/react';
import CustomTextField from '../../../../elements/CustomTextField/CustomTextField';
import RowItem from '../Sections/RowItem/RowItemProducts';
import CustomButton from '../../../../elements/CustomButton/CustomButton';
import {GetApp} from '@material-ui/icons';

import {endpoints} from '../../api';
import type {Pagination} from '../../../../entities';
import type {DashboardAction, ProductLessStockData} from '../../duck';

import type {RootState} from '../../../../modules';

export default connect(
  (state: RootState) => ({
    stockProductsPaginated: state.dashboard.stockProductsPaginated,
    stockProductsPagination: state.dashboard.stockProductsPagination,
    isFetchingStockProductsPaginated:
      state.dashboard.isFetchingStockProductsPaginated,
  }),
  null
)(LessStockProductsModal);

const useStyles = makeStyles(theme => ({
  customModal: {
    '& .MuiPaper-root': {
      padding: '16px!important',
      maxHeight: 580,
    },
  },
  root: {
    width: 520,
    maxWidth: '100%',
  },
  title: {
    color: colors.grey900,
    fontWeight: 600,
    fontSize: 18,
    textAlign: 'center',
  },
  divider: {
    backgroundColor: '#E8E8ED',
    margin: theme.spacing(2, 0),
  },
  groupFilter: {
    display: 'flex',
    alignItems: 'center',
  },
  wrapHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  wrapFilter: {
    display: 'flex',
    alignItems: 'center',
    width: '100%',
    justifyContent: 'space-between',
  },
  wrapItems: {
    maxHeight: '400px',
    overflowX: 'hidden',
    overflowY: 'auto',
    margin: theme.spacing(2, 0),
    minHeight: '200px',
    [theme.breakpoints.up('md')]: {
      // minHeight: 'unset',
      maxHeight: '300px',
    },
  },
  advice: {
    fontSize: 14,
    color: colors.grey700,
  },
  rowItem: {
    margin: 0,
  },
  inputStock: {
    width: 70,
  },
  titleForm: {
    display: 'flex',
    alignItems: 'center',
  },
  text: {
    color: colors.grey700,
    fontWeight: 500,
    fontSize: 12,
  },
  button: {
    borderRadius: '0px',
    width: '100%',
  },
}));

type Props = {
  stockProductsPaginated?: ProductLessStockData[],
  open: boolean,
  setOpen: (v: boolean) => void,
  stockProductsPagination?: Pagination,
  fetchLessStockProducts: (payload: DashboardAction) => void,
  isFetchingStockProductsPaginated?: boolean,
};
function LessStockProductsModal(props: Props) {
  const {
    stockProductsPaginated,
    open,
    setOpen,
    fetchLessStockProducts,
    stockProductsPagination,
    isFetchingStockProductsPaginated,
  } = props;

  const [minStock, setMinStock] = useState('50');
  const debouncedMinStock = useDebounce(minStock, 500);

  const classes = useStyles();

  const buildQueryParamsCallback = useCallback(
    (reset?: boolean = true) => {
      let params: {[key: string]: any} = {
        paginated: true,
        reset: !!reset,
      };
      if (debouncedMinStock) {
        params.stock = debouncedMinStock;
      }
      return params;
    },
    [debouncedMinStock]
  );

  const fetchStockProductsCallback = useCallback(
    (reset?: boolean) => {
      const queryFilter = buildQueryParamsCallback(reset);
      if (queryFilter) {
        fetchLessStockProducts({queryFilter});
      }
    },
    [buildQueryParamsCallback, fetchLessStockProducts]
  );

  const handleAddPage = () => {
    if (
      !isFetchingStockProductsPaginated &&
      stockProductsPagination &&
      stockProductsPagination.total > stockProductsPagination.size &&
      stockProductsPaginated &&
      stockProductsPaginated.length < stockProductsPagination.total
    ) {
      fetchStockProductsCallback(false);
    }
  };

  const promotionItemsRef = useRef();
  const onScroll = () => {
    if (promotionItemsRef.current) {
      const {scrollTop, scrollHeight, clientHeight} = promotionItemsRef.current;
      if (scrollTop + clientHeight === scrollHeight) {
        handleAddPage();
      }
    }
  };

  const fileDownloadUrl = useMemo(() => {
    const queryParams = {stock: minStock, mode: 'xlsx'};
    return `${
      endpoints.GET_SUMMARY_LESS_STOCK_PRODUCTS
    }?${queryString.stringify(queryParams)}`;
  }, [minStock]);

  useEffect(() => {
    if (open) {
      fetchStockProductsCallback();
    }
  }, [fetchStockProductsCallback, open]);

  return (
    <CustomModal
      open={open}
      handleCloseModal={() => setOpen(false)}
      className={classes.customModal}
    >
      <div className={classes.root}>
        <Typography className={classes.title}>
          Productos con menos stock
        </Typography>
        <Divider className={classes.divider} />

        <div className={classes.wrapHeader}>
          <div className={classes.titleForm}>
            <Typography className={classes.text}>
              Mostrar productos con menos de
            </Typography>
            <form style={{margin: '0 8px'}} onSubmit={e => e.preventDefault()}>
              <CustomTextField
                //   type="number"
                className={classes.inputStock}
                value={minStock}
                onChange={e => {
                  const stockNumber = Number(e.target.value);

                  if (isNaN(stockNumber) || stockNumber < 0) {
                    setMinStock('0');
                    return;
                  }

                  setMinStock(stockNumber.toString());
                }}
              />
            </form>
            <Typography className={classes.text}>unidades</Typography>
          </div>
          <CustomButton
            href={fileDownloadUrl}
            className={classes.btnDownload}
            variant="contained"
            target="_blank"
            disabled={isFetchingStockProductsPaginated}
            rel="noopener noreferrer"
          >
            <GetApp fontSize="small" />
          </CustomButton>
        </div>
        <div
          className={classes.wrapItems}
          onScroll={onScroll}
          ref={promotionItemsRef}
        >
          <Grid container spacing={2}>
            {stockProductsPaginated && stockProductsPaginated.length ? (
              stockProductsPaginated.map((p: ProductLessStockData, idx) => (
                <Grid item xs={12} key={idx}>
                  <CustomButton
                    className={classes.button}
                    to={`/products/${p.productId}`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    <RowItem
                      className={classes.rowItem}
                      id={idx}
                      text="disponibles"
                      name={p.name}
                      imageId={p.imageId}
                      number={p.stock}
                    />
                  </CustomButton>
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Typography className={classes.advice}>
                  No existen productos{' '}
                  {!isNaN(minStock) && `con mínimo ${minStock} de stock.`}
                </Typography>
              </Grid>
            )}
            {isFetchingStockProductsPaginated && (
              <Grid item xs={12}>
                <div
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress color="primary" />
                </div>
              </Grid>
            )}
          </Grid>
        </div>
      </div>
    </CustomModal>
  );
}

LessStockProductsModal.propTypes = {
  open: PropTypes.bool.isRequired,
  setOpen: PropTypes.func.isRequired,
  fetchLessStockProducts: PropTypes.func.isRequired,
  stockProductsPagination: PropTypes.object,
  stockProductsPaginated: PropTypes.array,
  isFetchingStockProductsPaginated: PropTypes.bool,
};
